/**
 * Slew bearings ↔ display "position metres" (PLAN §1: everything internal is in slew degrees;
 * position metres are a display conversion at the scale's reference radius). The same formulas
 * as `SectorConfig.position_m` / `bearing_at_position_m` in the API.
 */

export interface ArcScale {
  startBearingDeg: number;
  direction: 1 | -1;
  referenceRadiusM: number;
}

const RAD = Math.PI / 180;

export function wrap360(deg: number): number {
  const w = deg % 360;
  return w < 0 ? w + 360 : w;
}

/** Degrees travelled from the arc start to `bearing` along the arc direction, [0, 360). */
export function offsetDeg(scale: ArcScale, bearing: number): number {
  return wrap360((bearing - scale.startBearingDeg) * scale.direction);
}

export function positionM(scale: ArcScale, bearing: number): number {
  return offsetDeg(scale, bearing) * RAD * scale.referenceRadiusM;
}

export function bearingAtPositionM(scale: ArcScale, metres: number): number {
  return wrap360(scale.startBearingDeg + (scale.direction * metres) / (RAD * scale.referenceRadiusM));
}

export function scaleOf(site: {
  arc: { start_bearing_deg: number; direction: number };
  sectors: { reference_radius_m: number };
}): ArcScale {
  return {
    startBearingDeg: site.arc.start_bearing_deg,
    direction: site.arc.direction >= 0 ? 1 : -1,
    referenceRadiusM: site.sectors.reference_radius_m,
  };
}

export function formatBearing(deg: number): string {
  return `${wrap360(deg).toFixed(1)}°`;
}

export function formatMetres(m: number): string {
  return `${m.toFixed(2)} m`;
}
